import React, { useState, useRef, useEffect } from 'react';
import '../../styles-v2/forms/v2-multi-select.css';

const MultiSelectV2 = ({
  options = [],
  value = [],
  onChange,
  name = 'Select',
  placeholder,
  isDisabled = false,
  maxChipsDisplay = 2,
  className = '',
  error = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const visibleChips = value.slice(0, maxChipsDisplay);
  const overflowCount = value.length - maxChipsDisplay;

  const handleRemoveChip = (e, chipValue) => {
    e.stopPropagation();
    const newValue = value.filter((item) => item.value !== chipValue);
    onChange(newValue);
  };

  const handleClearAll = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  const handleSelectAll = (e) => {
    e.stopPropagation();
    onChange(options);
  };

  const handleClearAllInDropdown = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div
      ref={dropdownRef}
      className={`v2-multi-select ${className} ${error ? 'v2-multi-select--error' : ''} ${
        isDisabled ? 'v2-multi-select--disabled' : ''
      }`}
    >
      <div
        className={`v2-multi-select__trigger ${isOpen ? 'v2-multi-select__trigger--open' : ''}`}
        onClick={() => !isDisabled && setIsOpen(!isOpen)}
      >
        <div className="v2-multi-select__value">
          {value.length === 0 ? (
            <span className="v2-multi-select__placeholder">{placeholder || `Select ${name}`}</span>
          ) : (
            <div className="v2-multi-select__chips">
              {visibleChips.map((item) => (
                <div key={item.value} className="v2-multi-select__chip">
                  <span className="v2-multi-select__chip-label">{item.label}</span>
                  <button
                    type="button"
                    className="v2-multi-select__chip-remove"
                    onClick={(e) => handleRemoveChip(e, item.value)}
                    disabled={isDisabled}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
              ))}
              {overflowCount > 0 && (
                <div className="v2-multi-select__chip v2-multi-select__chip--overflow">
                  <span className="v2-multi-select__chip-label">+{overflowCount}</span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="v2-multi-select__actions">
          {value.length > 0 && !isDisabled && (
            <button
              type="button"
              className="v2-multi-select__clear"
              onClick={handleClearAll}
              title="Clear all"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          )}
          <div className={`v2-multi-select__arrow ${isOpen ? 'v2-multi-select__arrow--open' : ''}`}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="v2-multi-select__dropdown">
          <div className="v2-multi-select__header">
            <button
              type="button"
              className="v2-multi-select__header-action"
              onClick={handleSelectAll}
            >
              Select All
            </button>
            <span className="v2-multi-select__header-divider">|</span>
            <button
              type="button"
              className="v2-multi-select__header-action"
              onClick={handleClearAllInDropdown}
            >
              Clear All
            </button>
          </div>
          <div className="v2-multi-select__options">
            {options.map((option) => {
              const isSelected = value.some((item) => item.value === option.value);
              return (
                <div
                  key={option.value}
                  className={`v2-multi-select__option ${
                    isSelected ? 'v2-multi-select__option--selected' : ''
                  }`}
                  onClick={() => {
                    if (isSelected) {
                      onChange(value.filter((item) => item.value !== option.value));
                    } else {
                      onChange([...value, option]);
                    }
                  }}
                >
                  <div className="v2-multi-select__checkbox">
                    {isSelected && (
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                      >
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    )}
                  </div>
                  <span className="v2-multi-select__option-label">{option.label}</span>
                </div>
              );
            })}
            {options.length === 0 && (
              <div className="v2-multi-select__empty">No options available</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectV2;
